import React from 'react';
import { csvCell } from './csvCell';

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormField {
  id: string;
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  options?: (string | FormFieldOption)[];
}

/**
 * Parses and formats Special Request / Bulk Request response values (JSON or plain text)
 * into clean, human-readable "Field Name : Value" key-value pairs.
 */
export function renderFormattedResponseValue(
  responseValue: string | null | undefined,
  formFields?: FormField[]
): React.ReactNode {
  if (!responseValue || !responseValue.trim()) {
    return <span className="text-slate-400 italic">No text response submitted.</span>;
  }

  const trimmed = responseValue.trim();

  // Try parsing JSON object or array
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      const parsed = JSON.parse(trimmed);

      // Map field IDs to labels if formFields provided
      const labelMap = new Map<string, string>();
      if (Array.isArray(formFields)) {
        formFields.forEach(f => {
          if (f.id && f.label) labelMap.set(f.id, f.label);
        });
      }

      // Case A: Array of items [{ label/field/key, value }]
      if (Array.isArray(parsed)) {
        if (parsed.length === 0) {
          return <span className="text-slate-400 italic">Empty response.</span>;
        }
        return (
          <div className="space-y-2">
            {parsed.map((item, idx) => {
              if (item && typeof item === 'object') {
                const label = item.label || item.field || item.key || item.name || `Field ${idx + 1}`;
                const val = typeof item.value !== 'undefined' ? String(item.value) : JSON.stringify(item);
                return (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between p-2.5 rounded-xl bg-white border border-slate-200/80 shadow-3xs text-xs">
                    <span className="font-bold text-slate-700 shrink-0 mr-2">{label} :</span>
                    <span className="text-slate-800 font-semibold break-words mt-0.5 sm:mt-0">{val}</span>
                  </div>
                );
              }
              return (
                <div key={idx} className="text-xs text-slate-800 font-medium">
                  {String(item)}
                </div>
              );
            })}
          </div>
        );
      }

      // Case B: Key-Value Object
      if (typeof parsed === 'object' && parsed !== null) {
        const keys = Object.keys(parsed);
        if (keys.length === 0) {
          return <span className="text-slate-400 italic">Empty response.</span>;
        }

        return (
          <div className="space-y-2">
            {keys.map((rawKey) => {
              let displayLabel = labelMap.get(rawKey) || rawKey;
              // Clean up raw field IDs or camelCase/snake_case keys if no label in labelMap
              if (!labelMap.has(rawKey)) {
                displayLabel = displayLabel
                  .replace(/_/g, ' ')
                  .replace(/([a-z])([A-Z])/g, '$1 $2')
                  .replace(/\b\w/g, char => char.toUpperCase());
              }

              const rawVal = parsed[rawKey];
              let displayValue = '';
              if (Array.isArray(rawVal)) {
                displayValue = rawVal.join(', ');
              } else if (typeof rawVal === 'object' && rawVal !== null) {
                displayValue = JSON.stringify(rawVal);
              } else {
                displayValue = String(rawVal ?? '');
              }

              return (
                <div key={rawKey} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between p-2.5 rounded-xl bg-white border border-slate-200/80 shadow-3xs text-xs">
                  <span className="font-bold text-slate-700 shrink-0 mr-2">{displayLabel} :</span>
                  <span className="text-slate-800 font-semibold break-words mt-0.5 sm:mt-0">{displayValue}</span>
                </div>
              );
            })}
          </div>
        );
      }
    } catch {
      // Fallback to plain text if JSON parse fails
    }
  }

  // Plain text fallback
  return (
    <div className="text-xs text-slate-800 font-medium whitespace-pre-wrap leading-relaxed">
      {responseValue}
    </div>
  );
}

/**
 * Helper to build CSV rows with dedicated per-question columns (Zoho Form style)
 */
export function buildDynamicFormCsvRows(
  selectedBulkReq: any,
  recipientsData: any[]
): { csvHeaders: string[]; csvRows: string[][] } {
  // 1. Gather all question columns
  const questionMap = new Map<string, { id: string; label: string }>();

  // Check formFields on template/request
  if (selectedBulkReq && Array.isArray(selectedBulkReq.formFields)) {
    selectedBulkReq.formFields.forEach((f: any) => {
      const id = f.id || f.key || f.label;
      const label = f.label || f.id || 'Question';
      if (id && !questionMap.has(id)) {
        questionMap.set(id, { id, label });
      }
    });
  }

  // Scan recipients to find any additional response keys
  recipientsData.forEach(rec => {
    if (!rec.responseValue) return;
    const trimmed = String(rec.responseValue).trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
          Object.keys(parsed).forEach(key => {
            let matched = false;
            for (const [, q] of questionMap) {
              if (q.id === key || q.label === key) {
                matched = true;
                break;
              }
            }
            if (!matched) {
              const label = key
                .replace(/_/g, ' ')
                .replace(/([a-z])([A-Z])/g, '$1 $2')
                .replace(/\b\w/g, char => char.toUpperCase());
              questionMap.set(key, { id: key, label });
            }
          });
        }
      } catch {
        // Plain text
      }
    }
  });

  const questionList = Array.from(questionMap.values());

  // Base Headers
  const csvHeaders = [
    'Applicant Name',
    'Applicant Email',
    'Applicant ID',
    'Status',
    'Sent Date/Time',
    'Response Date/Time',
  ];

  if (questionList.length > 0) {
    questionList.forEach(q => csvHeaders.push(q.label));
  } else {
    csvHeaders.push('Response Value');
  }

  csvHeaders.push('Uploaded Files');

  // Helper to escape CSV field
  // Wave 5 (M-05): shared csvCell — quoting + formula-injection guard.
  const escapeCsv = (val: string | null | undefined): string => csvCell(val);

  // Build rows
  const csvRows = recipientsData.map(rec => {
    const isCompleted = rec.status === 'Submitted' || rec.status === 'Completed';
    const createdAtStr = rec.createdAt ? new Date(rec.createdAt).toLocaleString() : '';
    const uploadedAtStr = rec.uploadedAt ? new Date(rec.uploadedAt).toLocaleString() : '';
    const fileNames = rec.responseFiles && Array.isArray(rec.responseFiles)
      ? rec.responseFiles.map((f: any) => f.docName ? `[${f.docName}] ${f.name} (${f.url})` : `${f.name} (${f.url})`).join('; ')
      : '';

    const row: string[] = [
      escapeCsv(rec.applicantName),
      escapeCsv(rec.applicantEmail),
      escapeCsv(rec.applicantId),
      isCompleted ? '"Responded"' : '"Pending Response"',
      escapeCsv(createdAtStr),
      escapeCsv(uploadedAtStr),
    ];

    // Extract answers for questions
    if (questionList.length > 0) {
      let parsedResponse: Record<string, any> | null = null;
      if (rec.responseValue) {
        const trimmed = String(rec.responseValue).trim();
        if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
          try { parsedResponse = JSON.parse(trimmed); } catch {}
        }
      }

      questionList.forEach(q => {
        let answer = '';
        if (parsedResponse) {
          if (q.id in parsedResponse) {
            answer = parsedResponse[q.id];
          } else if (q.label in parsedResponse) {
            answer = parsedResponse[q.label];
          }
          if (Array.isArray(answer)) answer = answer.join(', ');
          else if (typeof answer === 'object' && answer !== null) answer = JSON.stringify(answer);
        } else if (rec.responseValue && questionList.length === 1) {
          answer = rec.responseValue;
        }
        row.push(escapeCsv(answer));
      });
    } else {
      row.push(escapeCsv(rec.responseValue || ''));
    }

    row.push(escapeCsv(fileNames));
    return row;
  });

  return { csvHeaders, csvRows };
}
